/**
 * Gerüst der Oberfläche: Kopfleiste, Navigation, Router.
 *
 * Die Anwendung besteht aus wenigen Ansichten mit einem klaren Zweck. Die
 * Adresszeile führt über einen Anker (#/kontakte), damit die Anwendung ohne
 * Serverkonfiguration auskommt und ein Neuladen die Ansicht behält.
 */

import { Status, aufAbmeldungReagieren, tokenLesen, tokenSetzen, meldungVerstaendlich } from './api.js';
import { el, ersetzen, knopf, eingabefeld, meldung, laedt, dialog } from './ui.js';
import { startAnsicht } from './ansichten/start.js';
import { kontakteAnsicht } from './ansichten/kontakte.js';
import { vorlagenAnsicht } from './ansichten/vorlagen.js';
import { kampagnenAnsicht, neueKampagneAnsicht } from './ansichten/kampagnen.js';
import { editorAnsicht } from './ansichten/editor.js';
import { verlassenGrund, verlassenFreigeben } from './verlassen-schutz.js';
import './stil.css';

/**
 * @typedef {object} Ansicht
 * @property {string} titel
 * @property {(behaelter: HTMLElement, parameter: Record<string,string>) => void|Promise<void>} zeichnen
 */

/** @type {{ muster: RegExp, ansicht: () => Ansicht, felder?: string[] }[]} */
const ROUTEN = [
  { muster: /^\/?$/, ansicht: startAnsicht },
  { muster: /^\/kontakte$/, ansicht: kontakteAnsicht },
  { muster: /^\/vorlagen$/, ansicht: vorlagenAnsicht },
  { muster: /^\/editor$/, ansicht: editorAnsicht },
  { muster: /^\/editor\/(\d+)$/, ansicht: editorAnsicht, felder: ['id'] },
  { muster: /^\/kampagnen$/, ansicht: kampagnenAnsicht },
  { muster: /^\/kampagnen\/neu$/, ansicht: neueKampagneAnsicht },
];

const NAVIGATION = [
  {
    titel: 'Aussendung',
    eintraege: [
      { pfad: '#/', zeichen: '◆', text: 'Übersicht' },
      { pfad: '#/kampagnen', zeichen: '➤', text: 'Aussendungen', zahl: 'kampagnen' },
    ],
  },
  {
    titel: 'Inhalte',
    eintraege: [
      { pfad: '#/vorlagen', zeichen: '▤', text: 'Vorlagen', zahl: 'vorlagen' },
      { pfad: '#/kontakte', zeichen: '☰', text: 'Empfänger', zahl: 'kontakte' },
    ],
  },
];

/** Zuletzt geladener Status, damit Zahlen in der Navigation stehen. */
let letzterStatus = null;

function anmeldeMaske() {
  const { feld, eingabe } = eingabefeld({
    beschriftung: 'Zugangstoken',
    art: 'text',
    platzhalter: 'Aus der .env der Installation',
    hinweis: 'Sie finden es in der Datei .env auf dem Server unter ZUGANGSTOKEN.',
  });

  const anmelden = () => {
    const wert = eingabe.value.trim();
    if (!wert) return;
    tokenSetzen(wert);
    starten();
  };

  eingabe.addEventListener('keydown', (ereignis) => {
    if (ereignis.key === 'Enter') anmelden();
  });

  ersetzen(
    document.body,
    el('div.dialog-huelle', [
      el('div.dialog', [
        el('header.dialog-kopf', [el('h2', { text: 'Anmeldung' })]),
        el('div.dialog-inhalt', [
          el('p', {
            text: 'Dieses Werkzeug verwaltet Mandantendaten und versendet in deren Namen. Bitte melden Sie sich an.',
          }),
          feld,
        ]),
        el('footer.dialog-fuss', [
          knopf('Anmelden', { art: 'haupt', beiKlick: anmelden }),
        ]),
      ]),
    ]),
  );
  eingabe.focus();
}

/**
 * @param {HTMLElement} navBehaelter
 * @param {string} aktiverPfad
 */
function navigationZeichnen(navBehaelter, aktiverPfad) {
  ersetzen(
    navBehaelter,
    ...NAVIGATION.map((gruppe) =>
      el('div.nav-gruppe', [
        el('div.nav-titel', { text: gruppe.titel }),
        ...gruppe.eintraege.map((eintrag) => {
          const aktiv = `#${aktiverPfad}` === eintrag.pfad || (aktiverPfad === '/' && eintrag.pfad === '#/');
          const zahl = eintrag.zahl ? letzterStatus?.bestand?.[eintrag.zahl] : undefined;
          return el(`a${aktiv ? '.aktiv' : ''}`, { href: eintrag.pfad }, [
            el('span.zeichen', { text: eintrag.zeichen }),
            el('span', { text: eintrag.text }),
            zahl !== undefined ? el('span.zahl', { text: String(zahl) }) : null,
          ]);
        }),
      ]),
    ),
  );
}

/**
 * @param {HTMLElement} ziel
 */
async function statusZeichnen(ziel) {
  try {
    letzterStatus = await Status.laden();
    const bereit = letzterStatus.bereit;
    ziel.className = `kopf-status ${bereit ? 'bereit' : 'gestoert'}`;
    ersetzen(
      ziel,
      el('span.ampel'),
      el('span', {
        text: bereit
          ? `Versandbereit über ${letzterStatus.provider}`
          : 'Versandweg nicht bereit',
        title: meldungVerstaendlich(letzterStatus.meldung),
      }),
    );

    // Die Zahlen in der Navigation stehen erst fest, wenn der Status geladen
    // ist - deshalb hier ein zweites Mal zeichnen.
    const nav = document.querySelector('.nav');
    if (nav instanceof HTMLElement) {
      navigationZeichnen(nav, (location.hash.replace(/^#/, '') || '/').split('?')[0] ?? '/');
    }
  } catch {
    ziel.className = 'kopf-status gestoert';
    ersetzen(ziel, el('span.ampel'), el('span', { text: 'Server nicht erreichbar' }));
  }
}

/** Pfad, auf dem die aktuelle Ansicht steht — Rückweg bei abgebrochener Navigation. */
let aktuellerPfad = location.hash || '#/';

/**
 * Merker für das Zurückdrehen der Adresszeile.
 *
 * Ein abgelehnter Wechsel muss die Adresse zurücksetzen, und das löst selbst
 * wieder ein hashchange aus. Würde dieser Rücksprung ganz normal durchlaufen,
 * zeichnete er die Ansicht NEU — und verwürfe damit genau die Arbeit, die die
 * Rückfrage gerade retten sollte. Er wird deshalb komplett übersprungen.
 */
let ruecksprungLaeuft = false;

/**
 * Fragt nach, bevor ungesicherte Arbeit verloren geht.
 * @param {string} grund
 * @param {string} zielPfad
 */
function verlustRueckfrage(grund, zielPfad) {
  const schliessen = dialog({
    titel: 'Ungespeicherte Änderungen',
    inhalt: [
      el('p', { text: grund }),
      el('p.feld-hinweis', {
        text: 'Wenn Sie jetzt wechseln, sind diese Änderungen verloren.',
      }),
    ],
    aktionen: [
      knopf('Hier bleiben', {
        art: 'haupt',
        beiKlick: () => schliessen(),
      }),
      knopf('Änderungen verwerfen', {
        beiKlick: () => {
          schliessen();
          // Der Wächter ist damit erledigt; der nächste Lauf zeichnet das Ziel.
          verlassenFreigeben();
          location.hash = zielPfad;
        },
      }),
    ],
  });
}

async function routen() {
  // Das Zurückdrehen der Adresse ist keine Navigation, sondern ihre Rücknahme.
  // Die bestehende Ansicht bleibt unangetastet stehen.
  if (ruecksprungLaeuft) {
    ruecksprungLaeuft = false;
    return;
  }

  const roh = location.hash.replace(/^#/, '') || '/';
  const [pfad] = roh.split('?');
  const inhalt = document.querySelector('.inhalt');
  const nav = document.querySelector('.nav');
  if (!(inhalt instanceof HTMLElement) || !(nav instanceof HTMLElement)) return;

  // Erst fragen, dann zeichnen: danach wäre die alte Ansicht samt Arbeit weg.
  const grund = verlassenGrund();
  if (grund) {
    const ziel = location.hash || '#/';
    if (ziel !== aktuellerPfad) {
      ruecksprungLaeuft = true;
      location.hash = aktuellerPfad;
      verlustRueckfrage(grund, ziel);
      return;
    }
  }
  verlassenFreigeben();
  aktuellerPfad = location.hash || '#/';

  navigationZeichnen(nav, pfad ?? '/');
  nav.classList.remove('offen');

  const treffer = ROUTEN.find((route) => route.muster.test(pfad ?? '/'));
  if (!treffer) {
    ersetzen(
      inhalt,
      el('div.bahn', [meldung('warnung', 'Diese Seite gibt es nicht.')]),
    );
    return;
  }

  /** @type {Record<string, string>} */
  const parameter = {};
  const werte = (pfad ?? '/').match(treffer.muster);
  treffer.felder?.forEach((feld, index) => {
    parameter[feld] = werte?.[index + 1] ?? '';
  });

  const ansicht = treffer.ansicht();
  document.title = `${ansicht.titel} · Newsletter`;
  ersetzen(inhalt, laedt());

  try {
    await ansicht.zeichnen(inhalt, parameter);
  } catch (fehler) {
    ersetzen(
      inhalt,
      el('div.bahn', [
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Unbekannter Fehler'),
      ]),
    );
  }
}

function starten() {
  const kopfStatus = el('div.kopf-status');
  const navBehaelter = el('nav.nav');

  ersetzen(
    document.body,
    el('div.app', [
      el('header.kopf', [
        el('div.kopf-links', [
          knopf('☰', { beiKlick: () => navBehaelter.classList.toggle('offen') }),
          el('a.kopf-marke', { href: '#/' }, [
            el('img', { src: '/bilder/logo.png', alt: 'G und W Steuerberatung' }),
            el('span.wort', { text: 'Newsletter' }),
          ]),
        ]),
        el('div.kopf-rechts', [kopfStatus]),
      ]),
      navBehaelter,
      el('main.inhalt'),
    ]),
  );

  // Der Menüknopf ist nur auf schmalen Bildschirmen sichtbar.
  const menue = document.querySelector('.kopf-links .knopf');
  menue?.classList.add('menue-knopf');

  statusZeichnen(kopfStatus);
  routen();
}

aufAbmeldungReagieren(() => anmeldeMaske());
window.addEventListener('hashchange', routen);

// Neuladen und Schließen des Browserfensters gehen am Router vorbei. Der
// Browser zeigt dafür seine eigene Rückfrage; mehr ist an dieser Stelle nicht
// möglich, und es genügt.
window.addEventListener('beforeunload', (ereignis) => {
  if (!verlassenGrund()) return;
  ereignis.preventDefault();
  ereignis.returnValue = '';
});

if (tokenLesen()) {
  starten();
} else {
  anmeldeMaske();
}
